import { useState, useEffect } from "react";
import { Rocket, ShieldAlert, Zap, Users, Share2, Sparkles, Check, ChevronRight, Loader2, Download, Trash2, Twitter } from "lucide-react";
import { FlowGraph } from "@/lib/algo/graph";
import { useApp } from "@/context/AppContext";
import { fetchTokenMeta } from "@/lib/tokenMeta";
import { getStoredHeliosKey, getSolanaRpcUrl } from "@/lib/solanaRpc";


const STEPS = [
  { id: "launch", title: "Launch Token", icon: Rocket, desc: "Deploy from main wallet" },
  { id: "stealth", title: "Stealth Buys & Airdrops", icon: ShieldAlert, desc: "Airdrop to users through multiwalleting and extract wallets features" },
  { id: "momentum", title: "Momentum Buy", icon: Zap, desc: "Buy again on main wallet" },
  { id: "paid_groups", title: "Shill Paid Groups", icon: Users, desc: "Manage paid promotions" },
  { id: "socials", title: "Shill on Socials", icon: Share2, desc: "Embed X threads" },
  { id: "ai_socials", title: "AI Suggestions", icon: Sparkles, desc: "Generate shill content" },
];

export function PlaygroundWizard({
  graph,
  update,
  onLaunchClick,
}: {
  graph: FlowGraph;
  update: (fn: (draft: FlowGraph) => void) => void;
  onLaunchClick: () => void;
}) {
  const { caMintInput } = useApp() as any;
  const [currentStep, setCurrentStep] = useState("launch");
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedCount, setExtractedCount] = useState(0);
  const [foreignMeta, setForeignMeta] = useState<{name: string, image: string} | null>(null);

  useEffect(() => {
    if (caMintInput) {
      fetchTokenMeta(caMintInput).then(meta => {
        if (meta) {
          setForeignMeta({ name: meta.name || "Unknown", image: meta.imageUri || "" });
        }
      }).catch(() => {});
    } else {
      setForeignMeta(null);
    }
  }, [caMintInput]);

  const [paidGroups, setPaidGroups] = useState<{ id: string; name: string; paid: boolean; scheduled: string }[]>([
    { id: "1", name: "Solana Whale Calls", paid: true, scheduled: "ASAP" },
    { id: "2", name: "Degen Alpha Group", paid: false, scheduled: "TBD" },
  ]);
  const [xLink, setXLink] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    // Simulate generation for now
    setTimeout(() => {
      setAiSuggestions([
        `Just launched $${graph.name || 'TOKEN'}! The dev team is cooking something massive. Don't fade this one! 🚀🔥`,
        `$${graph.name || 'TOKEN'} is sending! Real utility, based dev. Join the telegram before we hit millions! 💎🙌`,
      ]);
      setIsGenerating(false);
    }, 1500);
  };

  const handleExtractHolders = async () => {
    if (!caMintInput) return;
    setIsExtracting(true);
    setExtractedCount(0);
    
    try {
      const meta = await fetchTokenMeta(caMintInput).catch(() => null);
      if (meta) {
        setForeignMeta({ name: meta.name || "Unknown", image: meta.imageUri || "" });
      }

      const headers: Record<string, string> = {};
      const heliosKey = getStoredHeliosKey();
      if (heliosKey && heliosKey !== "backend-configured") {
        headers["x-helius-api-key"] = heliosKey;
      }
      const rpcUrl = getSolanaRpcUrl();
      if (rpcUrl && !rpcUrl.startsWith("/sol-rpc")) {
        headers["x-solana-rpc-url"] = rpcUrl;
      }

      const res = await fetch(`/api/extract-holders/${caMintInput}`, { headers });
      if (!res.body) throw new Error("No readable stream");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const newRecipients: any[] = [];
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const data = JSON.parse(line);
            newRecipients.push({ address: data.address, amount: data.amount, pct: data.pct });
            setExtractedCount(newRecipients.length);
          } catch(e) {}
        }
      }
      
      update(draft => {
        if (!draft.airdropLists) draft.airdropLists = [];
        draft.airdropLists.push({
          id: `ext_${Date.now()}`,
          name: meta?.name ? `Saved: ${meta.name}` : `Saved: ${caMintInput.slice(0,6)}`,
          sourceMint: caMintInput,
          recipients: newRecipients
        });
      });
      
    } catch (e) {
      console.error(e);
    } finally {
      setIsExtracting(false);
    }
  };

  const currentIndex = STEPS.findIndex(s => s.id === currentStep);

  return (
    <div className="flex h-full text-white bg-[#0a0a14]">
      {/* Sidebar Stepper */}
      <div className="w-64 border-r border-white/10 p-4 flex flex-col gap-2 overflow-y-auto">
        <h2 className="text-sm font-black uppercase tracking-widest text-emerald-400 mb-4 px-2">Launch Protocol</h2>
        {STEPS.map((step, idx) => {
          const isActive = currentStep === step.id;
          const isPast = currentIndex > idx;
          return (
            <button
              key={step.id}
              onClick={() => setCurrentStep(step.id)}
              className={`flex items-center gap-3 p-3 rounded-xl transition-all text-left group ${
                isActive ? "bg-emerald-500/20 border border-emerald-500/50" : "bg-white/5 border border-white/5 hover:bg-white/10"
              }`}
            >
              <div className={`p-2 rounded-lg ${isActive ? "bg-emerald-500 text-black" : isPast ? "bg-white/20 text-emerald-400" : "bg-white/10 text-white/50"}`}>
                {isPast ? <Check size={16} /> : <step.icon size={16} />}
              </div>
              <div className="flex-1">
                <div className={`font-bold text-sm ${isActive ? "text-emerald-300" : "text-white/80"}`}>{step.title}</div>
                <div className="text-[10px] text-white/40">{step.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 p-8 overflow-y-auto relative">
        <div className="max-w-3xl mx-auto">
          {currentStep === "launch" && (
            <div className="space-y-6 animate-in fade-in zoom-in-95">
              <h1 className="text-2xl font-black">Step 1: Launch from Main Wallet</h1>
              <p className="text-white/60">Configure your token deployment and initial dev buy. This will originate from your main connected wallet.</p>
              
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-white/50 mb-1 block">Token Name / Mint</label>
                  <input
                    type="text"
                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 outline-none focus:border-emerald-500 transition-colors"
                    value={graph.name || ""}
                    onChange={(e) => update(d => { d.name = e.target.value; })}
                    placeholder="Enter Token Name"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-white/50 mb-1 block">Dev Buy (SOL)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 outline-none focus:border-emerald-500 transition-colors"
                    value={graph.devBuySol}
                    onChange={(e) => update(d => { d.devBuySol = Math.max(0, Number(e.target.value)); })}
                  />
                </div>
                <button className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-lg rounded-lg transition-colors mt-4 shadow-[0_0_20px_rgba(16,185,129,0.3)]" onClick={onLaunchClick}>
                  Deploy & Launch
                </button>
              </div>
            </div>
          )}

          {currentStep === "stealth" && (
            <div className="space-y-6 animate-in fade-in zoom-in-95">
              <h1 className="text-2xl font-black">Step 2: Stealth Buys & Airdrops</h1>
              <p className="text-white/60">Configure side wallets to buy stealthily, or extract large wallet lists from foreign tokens to airdrop.</p>
              
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg">Side Wallets</h3>
                  <button className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded text-xs font-bold transition-colors">
                    + Add Wallet
                  </button>
                </div>
                <div className="space-y-2">
                  {graph.wallets.filter(w => w.id !== 'launch').map(w => (
                    <div key={w.id} className="flex items-center justify-between p-3 bg-black/40 rounded-lg border border-white/5">
                      <div className="font-mono">{w.label}</div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-white/40">{w.fundingSol} SOL Funded</span>
                        <button className="px-3 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 rounded hover:bg-emerald-500/30 text-xs transition-colors">
                          Execute Buy
                        </button>
                      </div>
                    </div>
                  ))}
                  {graph.wallets.filter(w => w.id !== 'launch').length === 0 && (
                    <div className="text-center p-8 text-white/30 border border-dashed border-white/10 rounded-lg">
                      No side wallets configured. Add wallets in the advanced editor or click above.
                    </div>
                  )}
                </div>
              </div>

              {/* Airdrops Extraction */}
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg">Airdrop Wallet Extraction</h3>
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-[10px] font-bold tracking-widest uppercase">
                    300K+ Supported
                  </span>
                </div>
                <p className="text-sm text-white/50">
                  Search a token using the global search (top right) and click "Open in Workspace". Then click below to extract its holders directly into an airdrop list.
                </p>

                {caMintInput ? (
                  <div className="flex items-center justify-between p-4 bg-black/40 border border-white/10 rounded-lg">
                    <div className="flex items-center gap-3">
                      {foreignMeta?.image ? (
                        <img src={foreignMeta.image} className="size-8 rounded-full" />
                      ) : (
                        <div className="size-8 rounded-full bg-white/10 flex items-center justify-center font-bold">?</div>
                      )}
                      <div>
                        <div className="font-bold">{foreignMeta?.name || "Foreign Token"}</div>
                        <div className="font-mono text-xs text-white/40">{caMintInput.slice(0,8)}...</div>
                      </div>
                    </div>
                    
                    <button 
                      onClick={handleExtractHolders} 
                      disabled={isExtracting}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                    >
                      {isExtracting ? (
                        <>
                          <Loader2 className="size-4 animate-spin" />
                          <span>Saving ({extractedCount.toLocaleString()})...</span>
                        </>
                      ) : (
                        <>
                          <Download className="size-4" />
                          <span>Extract Holders</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="p-4 bg-black/40 border border-dashed border-white/10 rounded-lg text-center text-white/40 text-sm">
                    No foreign token selected. Use Spotlight Search (Ctrl+K) to find a token.
                  </div>
                )}

                {/* Display extracted lists */}
                {graph.airdropLists && graph.airdropLists.length > 0 && (
                  <div className="pt-4 space-y-2 border-t border-white/10">
                    <h4 className="text-xs font-bold uppercase text-white/50 mb-2">Saved Airdrop Lists</h4>
                    {graph.airdropLists.map(list => (
                      <div key={list.id} className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-lg">
                        <div>
                          <div className="font-bold text-sm text-emerald-400">{list.name}</div>
                          <div className="text-xs text-white/50">{list.recipients.length.toLocaleString()} wallets saved</div>
                        </div>
                        <button 
                          onClick={() => update(d => { d.airdropLists = d.airdropLists?.filter(l => l.id !== list.id); })}
                          className="p-2 text-white/40 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === "momentum" && (
            <div className="space-y-6 animate-in fade-in zoom-in-95">
              <h1 className="text-2xl font-black">Step 3: Main Wallet Momentum Buy</h1>
              <p className="text-white/60">Execute a large secondary buy from the main wallet to simulate momentum and trigger scanner alerts.</p>
              
              <div className="p-8 bg-gradient-to-br from-emerald-900/20 to-black border border-emerald-500/20 rounded-xl text-center space-y-6">
                <Zap size={48} className="mx-auto text-emerald-400 animate-pulse" />
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-white/50 mb-2 block">Momentum Buy Amount (SOL)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    defaultValue={5}
                    className="w-32 bg-black/60 border border-emerald-500/50 rounded-lg p-3 outline-none focus:border-emerald-400 transition-colors text-center text-xl font-bold text-emerald-300"
                  />
                </div>
                <button className="px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-lg rounded-lg transition-colors shadow-[0_0_30px_rgba(16,185,129,0.4)]">
                  Execute Momentum Buy NOW
                </button>
              </div>
            </div>
          )}

          {currentStep === "paid_groups" && (
            <div className="space-y-6 animate-in fade-in zoom-in-95">
              <h1 className="text-2xl font-black">Step 4: Shill Paid Groups</h1>
              <p className="text-white/60">Manage your paid Telegram and Discord group promotions.</p>
              
              <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-black/40 text-white/40 uppercase tracking-wider text-[10px] font-bold">
                    <tr>
                      <th className="p-4">Group Name</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Schedule</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {paidGroups.map(g => (
                      <tr key={g.id} className="hover:bg-white/[0.02]">
                        <td className="p-4 font-bold">{g.name}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${g.paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                            {g.paid ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                        <td className="p-4 text-white/60">{g.scheduled}</td>
                        <td className="p-4 text-right">
                          <button className="text-white/40 hover:text-white transition-colors" onClick={() => {
                            setPaidGroups(prev => prev.filter(x => x.id !== g.id));
                          }}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-4 border-t border-white/5 bg-black/20">
                  <button className="text-emerald-400 hover:text-emerald-300 font-bold text-xs uppercase tracking-wider">
                    + Add New Group
                  </button>
                </div>
              </div>
            </div>
          )}

          {currentStep === "socials" && (
            <div className="space-y-6 animate-in fade-in zoom-in-95">
              <h1 className="text-2xl font-black">Step 5: Social Media Embedded Shill</h1>
              <p className="text-white/60">Embed your X (Twitter) thread link to track engagement directly.</p>
              
              <div className="p-6 bg-white/5 border border-white/10 rounded-xl space-y-4">
                <div>
                <div className="flex justify-between items-end mb-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-white/50 block">X Post URL</label>
                  <button 
                    onClick={() => window.open('/api/auth/twitter', '_blank', 'width=600,height=700')}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1DA1F2] hover:bg-[#1A91DA] text-white text-[10px] font-black uppercase tracking-widest rounded-full transition-all"
                  >
                    <Twitter size={12} /> Connect X Account
                  </button>
                </div>
                  <input
                    type="text"
                    value={xLink}
                    onChange={(e) => setXLink(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg p-3 outline-none focus:border-emerald-500 transition-colors"
                    placeholder="https://x.com/your_handle/status/123456789"
                  />
                </div>
                {xLink ? (
                  <div className="p-8 border border-dashed border-white/20 rounded-lg text-center flex flex-col items-center justify-center gap-4 bg-black/40">
                    <Share2 size={32} className="text-white/20" />
                    <div className="text-white/60">X Embed Preview would render here.</div>
                    <a href={xLink} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline">Open Thread</a>
                  </div>
                ) : (
                  <div className="text-center p-8 text-white/30 text-sm">
                    Paste a link above to preview your thread.
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === "ai_socials" && (
            <div className="space-y-6 animate-in fade-in zoom-in-95">
              <h1 className="text-2xl font-black">Step 6: AI Social Suggestions</h1>
              <p className="text-white/60">Use AI to generate a stream of high-engagement shill tweets. Click to post instantly.</p>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="e.g. Write a hype tweet about our new meta..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg p-3 outline-none focus:border-emerald-500 transition-colors"
                  />
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="px-6 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-lg transition-colors flex items-center gap-2"
                  >
                    {isGenerating ? <Sparkles className="animate-spin" size={16} /> : <Sparkles size={16} />}
                    Generate
                  </button>
                </div>

                <div className="space-y-3 mt-6">
                  {aiSuggestions.map((sug, i) => (
                    <div key={i} className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-emerald-500/50 transition-colors group">
                      <p className="text-white/90 mb-4 whitespace-pre-wrap">{sug}</p>
                      <div className="flex justify-end">
                        <a
                          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(sug)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-4 py-2 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white rounded-full text-xs font-bold transition-colors flex items-center gap-2"
                        >
                          <Share2 size={12} /> Post to X
                        </a>
                      </div>
                    </div>
                  ))}
                  {aiSuggestions.length === 0 && !isGenerating && (
                    <div className="text-center p-8 text-white/30 border border-dashed border-white/10 rounded-lg">
                      Generate some ideas to see suggestions.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center mt-12 pt-6 border-t border-white/5">
            <button
              onClick={() => setCurrentStep(STEPS[currentIndex - 1]?.id)}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-white/40 hover:text-white disabled:opacity-0 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setCurrentStep(STEPS[currentIndex + 1]?.id)}
              disabled={currentIndex === STEPS.length - 1}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-0"
            >
              Next Step <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
